/**
 * THE BOARD — day, week, or month, and a day is always one tap away.
 *
 * Anthony, on mobile: "when I click today I should be able to select just
 * the calendar for today, this week selects the week etc. Then when I click
 * any day on the calendar it should shift to that day's view so you can get
 * to all the data."
 *
 * That is the right shape and it is not three screens. It is one range,
 * and the range is what you are choosing. So the picker chooses Day, Week
 * or Month, and tapping any date anywhere sets the range to Day on that
 * date. Drilling in is not a different mode you have to leave — it is the
 * same control landing on a smaller range.
 *
 * Above all of it: any job that believes it is booked and has no date. The
 * check constraint should make that impossible now, and the board asks
 * anyway, because "impossible" is a claim and this is the evidence.
 */

import { db } from "../data/client.ts";
import { settleJob } from "../data/payments.ts";
import { key, shopDay, shopDayKey, startOfShopDay, shopTime, shopZone }
  from "../ui/shopclock.ts";
import { STATUS_LABEL, vehicleName, type Status } from "../data/jobs.ts";
import { picker } from "../ui/picker.ts";
import { SPANS, type Span } from "../data/funnel.ts";
import { esc, money, mount } from "../ui/dom.ts";
import { shopWords } from "../ui/words.ts";

type Booked = {
  id: string;
  created_at: string; job_number: string; status: Status; title: string | null; scheduled_at: string | null;
  customers: { full_name: string; phone: string | null } | null;
  vehicles: { year: number | null; make: string | null; model: string | null; description: string | null } | null;
  line_items: Array<{ total: number }> | null;
  /** The second opinion on whether a job is finished. See the overdue box. */
  invoices: Array<{ id: string; status: string }> | null;
  job_technicians: Array<{ technician_id: string;
                           technicians: { name: string } | null }> | null;
};

const SEL = "id,job_number,status,title,scheduled_at,created_at," +
  "customers(full_name,phone)," +
  "vehicles(year,make,model,description),line_items(total),invoices(id,status)";

/**
 * WHOSE JOB IT IS. Anthony: "Jobs on schedule should show based on color
 * code if looking at calendar whos they are and by text in tile/list
 * view." A board that does not say who is doing the work is a board an
 * advisor has to open eight jobs to read.
 *
 * ONE embed, made inner when a technician is chosen. It used to be a plain
 * embed in SEL with a second `!inner` one bolted on beside it, and asking
 * PostgREST for the same relation twice earns a 400: "aggregate functions
 * are not allowed in FROM clause of their own query level". So the moment
 * anybody pressed a name on the crew bar the query failed, the board drew
 * from a null result, and the day looked empty. Anthony: "it seems to not
 * be sorting... correctly based on techs and times."
 */
const crewSel = (only: boolean) =>
  `${SEL},job_technicians${only ? "!inner" : ""}(technician_id,technicians(name))`;

/**
 * A COLOR PER TECHNICIAN, AND THE SAME ONE TOMORROW.
 *
 * Taken from the id rather than from their position in a list, so a
 * technician's color does not change the day somebody new is hired or
 * the day one of them is marked inactive. Twelve hues around the wheel,
 * far enough apart to tell at a glance on a month grid.
 *
 * Nobody assigned gets the neutral, because "unassigned" is a real state
 * worth spotting and giving it a color would hide it among the rest.
 */
const CREW_HUES = [8, 32, 52, 96, 140, 168, 190, 212, 236, 262, 288, 320];

export function crewHue(id: string | null | undefined): number | null {
  if (!id) return null;
  let n = 0;
  for (let i = 0; i < id.length; i++) n = (n * 31 + id.charCodeAt(i)) >>> 0;
  return CREW_HUES[n % CREW_HUES.length]!;
}

/** The people on a job, and the one whose color the chip takes. */
function crewOf(j: Booked): { id: string | null; names: string[] } {
  const on = (j.job_technicians ?? []).filter((t) => t?.technician_id);
  return {
    id: on[0]?.technician_id ?? null,
    names: on.map((t) => t.technicians?.name ?? "").filter(Boolean),
  };
}

const crewStyle = (id: string | null): string => {
  const h = crewHue(id);
  return h == null ? "" : ` style="--crew:${h}"`;
};

/**
 * The SAME today/week/month control the desk uses, from the same list.
 * Anthony: "Don't leave two different setups for today/week/month." Two
 * controls that mean the same thing are two things to learn and two places
 * for them to drift apart, so there is one definition and both screens
 * import it. "All time" is dropped here — a calendar of all time is not a
 * calendar.
 */
type Range = Exclude<Span, "all">;
/**
 * The word "Today" appeared TWICE on this bar — once on the stepper,
 * meaning "jump back to today", and once on the range picker, meaning
 * "show one day at a time". Side by side on a desk that reads as two
 * controls; stacked on a phone it reads as the same control drawn twice.
 *
 * The picker is choosing a SPAN, so it says so. Only here — "Today" is the
 * right word on the dashboard, where there is no stepper to collide with.
 */
const SPAN_WORD: Record<string, string> = { today: "Day", week: "Week", month: "Month" };
const RANGES = SPANS.filter((s) => s.key !== "all")
  .map((s) => ({ ...s, label: SPAN_WORD[s.key] ?? s.label })) as
  Array<{ key: Range; label: string }>;

// Opens on today, like the dashboard. A technician looking at the
// schedule wants to know where they are going next, not what the week
// holds.
let range: Range = "today";

/**
 * CALENDAR OR LIST, and it opens on the calendar.
 *
 * The month grid used to appear only when the range was Month, so
 * choosing Today took the calendar away entirely — you could not see the
 * shape of the week you were standing in. They are two ways of reading
 * the same days, not two different screens, so they share one control and
 * the period picker keeps working in both.
 *
 * A calendar shows the month the anchor falls in whatever the range says,
 * because a calendar of a single day is not a calendar. The range still
 * decides what the LIST shows, and tapping a date still drops you into
 * that day.
 */
type View = "calendar" | "list";
const VIEW_KEY = "view:schedule";

function viewNow(): View {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === "list" ? "list" : "calendar";
  } catch { return "calendar"; }
}
function setViewNow(v: View): void {
  try { localStorage.setItem(VIEW_KEY, v); } catch { /* a preference is not worth a broken page */ }
}
let anchor = new Date();          // any day inside the range being shown

/**
 * WHOSE WORK IS ON THE BOARD.
 *
 * Anthony: "schedule needs to sortable for anyone not a technician to view
 * jobs assigned and scheduled to a certain worker."
 *
 * A technician does not need this — row level security already limits them
 * to their own jobs, and the "On you" band answers it directly. It is the
 * advisor and the owner who need it, because they are looking at
 * everybody's day and the question they are actually asking is "what has
 * David got on Thursday".
 *
 * Empty means everybody, which is what the board has always shown, so
 * nobody who ignores this control notices it exists.
 */
/**
 * WHOSE BOARD, and it may be more than one.
 *
 * Anthony: "On calendar we should be able to select more than one rep."
 *
 * One name at a time answers "what has David got on Thursday". It does not
 * answer the question an advisor actually asks on a Monday, which is "what
 * have David and Mike got between them" — two technicians who share a van,
 * or the two who are in tomorrow while the third is off. Picking one, then
 * the other, then trying to hold both in your head is not a filter, it is
 * arithmetic.
 *
 * Empty means everybody, which is what the board has always shown, so
 * nobody who ignores this notices it exists.
 */
let who: string[] = [];
let whoNames = new Map<string, string>();   // so a pill survives an empty board

// The day is the SHOP's, wherever the person looking at it is standing.
// See src/domain/shopday.ts for why, and for the two kinds of Date; the
// zone itself is primed once at boot in src/ui/shopclock.ts.
const startOfDay = (d: Date) => shopDay(d);
const fromKey = (k: string) => new Date(`${k}T12:00:00`);
const time = shopTime;

/** The window the current choice covers, and what to call it. */
function window_(): { from: Date; to: Date; title: string } {
  const a = startOfDay(anchor);
  if (range === "today") {
    const today = shopDayKey(new Date());
    return { from: a, to: new Date(a.getFullYear(), a.getMonth(), a.getDate() + 1),
      title: key(a) === today ? "Today"
        : a.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) };
  }
  if (range === "week") {
    const from = new Date(a); from.setDate(a.getDate() - a.getDay());
    const to = new Date(from.getTime() + 7 * 86400000);
    return { from, to, title: `Week of ${from.toLocaleDateString("en-US", { month: "short", day: "numeric" })}` };
  }
  const from = new Date(a.getFullYear(), a.getMonth(), 1);
  const to = new Date(a.getFullYear(), a.getMonth() + 1, 1);
  return { from, to, title: from.toLocaleDateString("en-US", { month: "long", year: "numeric" }) };
}

/**
 * WHAT TO FETCH, which is not the same as what to show.
 *
 * The calendar draws a whole month whatever the range says, so it needs a
 * whole month of jobs. Reading only the range would have given a month
 * grid with one day in it — the calendar would have looked empty on every
 * day but the one selected, which reads as lost work rather than as a
 * narrow query.
 */
function fetchWindow(w: { from: Date; to: Date }): { from: Date; to: Date } {
  if (viewNow() !== "calendar") return w;
  const a = startOfDay(anchor);
  return {
    from: new Date(a.getFullYear(), a.getMonth(), 1),
    to: new Date(a.getFullYear(), a.getMonth() + 1, 1),
  };
}

const total = (j: Booked) => (j.line_items ?? []).reduce((n, l) => n + Number(l.total ?? 0), 0);

/**
 * One job, with enough on it to act without opening anything.
 *
 * `dated` is for the rows drawn outside the day board. "9:00" on its own
 * reads as this morning, which is exactly the misreading the boxes above
 * the board exist to stop, so those rows carry the day as well.
 */
function jobRow(j: Booked, dated = false): string {
  const slot = dated && j.scheduled_at
    ? `${new Date(j.scheduled_at).toLocaleDateString("en-US",
        { timeZone: shopZone(), month: "short", day: "numeric" })}<i>${
        esc(time(j.scheduled_at))}</i>`
    : esc(time(j.scheduled_at));
  /**
   * FINISHED WORK STAYS ON ITS DAY, AND LOOKS FINISHED.
   *
   * Anthony: "Completed jobs should still show here as paid for the day
   * selected."
   *
   * They were always fetched -- the board has never filtered by status --
   * but they read exactly like the work still to do, so a day with two
   * jobs left and three already paid looked like five jobs left. Done work
   * is dimmed and ticked. It is still there, still clickable, and no
   * longer competing for attention with the van that has not moved.
   */
  const done = j.status === "completed" || j.status === "invoiced"
    || j.status === "paid" || j.status === "cancelled";
  return `
    <a class="row${done ? " settled" : ""}" href="#/job/${j.id}">
      <div class="slot">${slot}</div>
      <div class="row-main">
        <div class="row-title"><span class="num">${esc(j.customers?.full_name ?? "No customer")}</span>
          ${crewOf(j).names.length
            ? `<span class="whochip"${crewStyle(crewOf(j).id)}>${
                esc(crewOf(j).names.join(", "))}</span>`
            : `<span class="whochip none">nobody on it</span>`}
          <span class="pill s-${esc(j.status)}">${
            j.status === "paid" ? "&#10003; " : ""}${
            esc(STATUS_LABEL[j.status] ?? j.status)}</span></div>
        <div class="row-sub">${esc(vehicleName(j.vehicles as never))}${
          j.title ? `<span class="dot">&middot;</span>${esc(j.title)}` : ""}</div>
      </div>
      <div class="row-side"><div class="amount">${money(total(j))}</div>
        <div class="when">${esc(j.job_number)}</div></div>
    </a>`;
}

/**
 * A month grid. Every date is a button that drops the range onto that day.
 *
 * ON A PHONE IT IS NOT A GRID. A 7x6 month at 375px is unreadable, so
 * the empty cells are hidden and what is left reads as an agenda — which
 * was fine until a quiet day. Anthony, tonight: "under jobs and schedule
 * now I can only see yesterdays not todays."
 *
 * He was right and it was this. The agenda starts at the 1st of the
 * month, so on 2 September the first thing on the screen was the 1st,
 * with nine jobs on it, and today — which has two — was below the fold.
 * The month you are IN should open where you are standing in it, so days
 * already gone are marked and dropped from the phone agenda unless you
 * have deliberately walked back to a past month.
 */
function monthGrid(byDay: Map<string, Booked[]>): string {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = new Date(first); start.setDate(1 - first.getDay());
  const today = shopDayKey(new Date());
  const now = startOfDay(new Date());
  // Only when the anchor is the month we are actually in. Walk back to
  // August and every day of it should still be there.
  const thisMonth = anchor.getFullYear() === now.getFullYear()
    && anchor.getMonth() === now.getMonth();
  const cells: string[] = [];

  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const k = key(d);
    const list = byDay.get(k) ?? [];
    const other = d.getMonth() !== anchor.getMonth();
    const gone = thisMonth && d < now;
    cells.push(`
      <button class="cal-cell${other ? " other" : ""}${k === today ? " today" : ""}${
        list.length ? " has" : ""}${gone ? " gone" : ""}"
              data-day="${k}">
        <span class="cal-date">${d.getDate()}</span>
        ${list.slice(0, 3).map((j) => {
          const c = crewOf(j);
          return `<span class="cal-chip s-${esc(j.status)}${c.id ? " crewed" : ""}"${
            crewStyle(c.id)} data-job="${esc(j.id)}" title="${
            esc([j.customers?.full_name, c.names.join(", ") || "nobody on it"]
              .filter(Boolean).join(" — "))}"><b>${
            esc(time(j.scheduled_at))}</b> ${
            esc((j.customers?.full_name ?? "—").split(" ")[0] ?? "")}</span>`;
        }).join("")}
        ${list.length > 3 ? `<span class="cal-more">+${list.length - 3}</span>` : ""}
      </button>`);
  }
  return `<div class="cal-dow">${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    .map((d) => `<div>${d}</div>`).join("")}</div>
    <div class="cal-grid">${cells.join("")}</div>`;
}

/** Day and week both read as a list, grouped by day. Week gets headers. */
function dayList(byDay: Map<string, Booked[]>, from: Date, to: Date, headers: boolean): string {
  const out: string[] = [];
  for (let d = new Date(from); d < to; d.setDate(d.getDate() + 1)) {
    const k = key(d);
    const list = byDay.get(k);
    if (!list?.length) continue;
    const label = fromKey(k).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
    out.push(`<div class="card">${headers
      ? `<div class="card-head">${esc(label)} &middot; ${list.length}</div>` : ""}
      ${list.map((j) => jobRow(j)).join("")}</div>`);
  }
  if (out.length) return out.join("");

  /**
   * AN EMPTY BOARD SAYS WHOSE IT IS.
   *
   * Anthony: "why do none show for Robert or me on schedule etc?" Neither
   * had anything booked that day, and the board said only "Nothing booked
   * in this day" -- which reads as the system having lost the work rather
   * than as the honest answer to the question actually being asked. With
   * a name filter on, it now says which name, and that it is only this
   * ${range} rather than everything they have.
   */
  const picked = [...whoNames.entries()].filter(([id]) => who.includes(id))
    .map(([, name]) => name);
  return `<div class="card"><div class="empty">${
    picked.length
      ? `Nothing booked for ${esc(picked.join(" or "))} in this ${range}. `
        + `Their finished work is on the Jobs screen.`
      : `Nothing booked in this ${range}.`}</div></div>`;
}

export async function schedulePage(): Promise<void> {
  const w = window_();

  /**
   * ONE BAR, NOT FOUR CONTROLS. Anthony: "for current page orientation
   * today/week/month the option that switches it stays saying today. It's
   * cluttered. Look to simplify yet retain all capabilities."
   *
   * It had a stepper whose middle button said "Today", beside a dropdown
   * labeled "Show" whose value also said "Today". Two controls, one word,
   * and neither of them obviously the one that changes what you are
   * looking at.
   *
   * Day, Week and Month become three buttons rather than a dropdown,
   * because a three-item menu is two clicks to do what one click should.
   * The arrows move next to the TITLE, which is the thing they move. And
   * the Today button only exists when you are not on today: a button that
   * does nothing nine times out of ten is the definition of clutter.
   *
   * Nothing is lost. Same three ranges, same stepping, same jump home.
   */
  const onToday = key(startOfDay(anchor)) === shopDayKey(new Date());

  /**
   * THE DAY YOU ARE LOOKING AT SITS UNDER THE WORD SCHEDULE, and the
   * arrows sit with the range buttons rather than around a Today.
   *
   * Anthony: "the arrows on mobile for the schedule bar can sit to the
   * left of the day week month selector. Just need arrows not the today
   * section between arrows. Right above this left to right where it says
   * schedule, it can say under schedule and before this row the time that
   * has been selected."
   *
   * So: the heading, the chosen day under it, then one row of controls
   * that reads left to right the way it works -- back, forward, then how
   * much to show. Today is a small link beside the date rather than a
   * button wedged between two arrows, because it is only ever pressed when
   * you have walked away from today.
   */
  mount(`<div class="page">
      <header class="page-head"><h1>Schedule</h1>
        <div class="schwhen">
          <p id="sch-title">${esc(w.title)}</p>
          ${onToday ? "" : `<button class="schhome" id="sch-today">Back to today</button>`}
        </div>
      </header>
      <!-- ONE BAR, LEFT TO RIGHT: back, forward, how much, how to look at
           it. It used to wrap to two rows on a phone with the calendar and
           list toggle stranded in the middle of the second, which reads as
           three separate controls rather than one bar. -->
      <div class="listbar schedbar">
        <div class="schsteps">
          <button class="schstep" id="sch-prev" aria-label="Back">&larr;</button>
          <button class="schstep" id="sch-next" aria-label="Forward">&rarr;</button>
        </div>
        <div class="viewtog wide" role="group" aria-label="How much to show">
          ${RANGES.map((r) => `<button class="${range === r.key ? "on" : ""}"
            data-range="${r.key}">${esc(r.label)}</button>`).join("")}
        </div>
        <div class="viewtog icons" role="group" aria-label="How to show the schedule">
          <button class="${viewNow() === "calendar" ? "on" : ""}" data-v="calendar"
            title="Calendar" aria-label="Calendar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>
          </button>
          <button class="${viewNow() === "list" ? "on" : ""}" data-v="list"
            title="List" aria-label="List">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
          </button>
        </div>
      </div>
      <!-- WHOSE WORK, AND WHICH COLOR THEY ARE. See crewBar(). -->
      <div id="crewbar"></div>
      <div id="board"><div class="loading">Reading&hellip;</div></div>
      <!-- BELOW the board, not above it. Put where it started, four of
           yesterday's stragglers outranked today's two jobs and the
           screen read as the wrong day. What you asked for comes first;
           what is overdue is a footnote you cannot miss, not a
           headline that hides the question. -->
      <div id="orphans"></div>
    </div>`);

  document.querySelectorAll<HTMLButtonElement>("[data-range]").forEach((b) =>
    b.addEventListener("click", () => {
      const k = (b.dataset["range"] as Range) ?? "today";
      range = k;
    // A CALENDAR OF ONE DAY IS NOT A CALENDAR — the grid draws a whole
    // month whatever the range, so choosing Day while looking at the
    // calendar changed nothing on screen. Asking for a day gives you the
    // day. Week and month leave the view alone.
    if (k === "today") setViewNow("list");
    void schedulePage();
    }));

  document.querySelectorAll<HTMLButtonElement>(".viewtog button").forEach((b) =>
    b.addEventListener("click", () => {
      const v = b.dataset["v"] as View;
      if (v === viewNow()) return;
      setViewNow(v);
      void schedulePage();
    }));

  const step = (dir: number) => {
    const a = new Date(anchor);
    if (range === "today") a.setDate(a.getDate() + dir);
    else if (range === "week") a.setDate(a.getDate() + 7 * dir);
    else a.setMonth(a.getMonth() + dir);
    anchor = a;
    void schedulePage();
  };
  document.getElementById("sch-prev")?.addEventListener("click", () => step(-1));
  document.getElementById("sch-next")?.addEventListener("click", () => step(1));
  document.getElementById("sch-today")?.addEventListener("click", () => {
    anchor = new Date(); range = "today"; void schedulePage();
  });

  const fw = fetchWindow(w);

  try {
    const sb = await db();
    /**
     * A BOOKING THAT FALLS OFF THE BOARD.
     *
     * Anthony: "I still have a hard time finding why a customer pops off
     * the schedule and such."
     *
     * Nothing deletes them. The day simply passes. A job booked for
     * Tuesday that nobody started is still `scheduled` on Wednesday, and
     * Wednesday's board does not draw Tuesday — so the customer is gone
     * from every screen anybody actually looks at while still being, as
     * far as the shop is concerned, booked in. Twelve of them were sitting
     * there when this was written, the oldest nine days back.
     *
     * There is a second way through the same hole: an in-progress job with
     * no date at all. It is not on the calendar because it has no day, and
     * it is not in the old "booked with no date" box because that box only
     * looked at `scheduled`.
     *
     * So both are fetched separately from the range, and shown ABOVE the
     * board whatever day you are looking at. The point is that they cannot
     * be scrolled past.
     */
    // The shop's midnight, as the instant it actually is. Asking for the
    // device's midnight is what put the evening's work on tomorrow.
    const dayStart = startOfShopDay(startOfDay(new Date())).toISOString();

    /**
     * WHOSE BOARD. An inner join through job_technicians when somebody is
     * chosen, so "what has David got on Thursday" is one question rather
     * than reading the whole day and squinting at the names. Nothing at
     * all when it is everybody, which is what it has always been.
     */
    const mineOnly = <T>(q: T): T => (who.length
      ? (q as never as { in: (a: string, b: string[]) => T })
          .in("job_technicians.technician_id", who)
      : q);
    const S = crewSel(who.length > 0);

    /**
     * WHO HAS WORKED LATELY, for the name bar only.
     *
     * Anthony: "all techs jobs from the last 7 days should be in there so
     * why do none show for Robert or me on schedule etc?"
     *
     * They were there. Neither of them had anything booked TODAY -- Robert
     * finished his last on the Wednesday, Anthony his on the Monday -- and
     * the name bar was built purely from the jobs already on screen. So on
     * a day they were not working, they did not appear on the bar at all,
     * and a technician who cannot find their own name reasonably concludes
     * their work has gone missing rather than that they are looking at the
     * wrong day.
     *
     * A separate, deliberately narrow query: ids and names over the last
     * seven days, whatever the board is currently showing. The BOARD still
     * shows only the day or week you asked for -- this changes who you can
     * ask about, not what is drawn.
     */
    const RECENT_DAYS = 7;
    const recentFrom = new Date(Date.now() - RECENT_DAYS * 864e5).toISOString();

    const [booked, dateless, overdue, adrift, lately] = await Promise.all([
      mineOnly(sb.from("jobs").select(S).not("scheduled_at", "is", null)
        .gte("scheduled_at", startOfShopDay(fw.from).toISOString())
        .lt("scheduled_at", startOfShopDay(fw.to).toISOString())
        .order("scheduled_at").limit(600)),
      mineOnly(sb.from("jobs").select(S).eq("status", "scheduled")
        .is("scheduled_at", null).order("created_at", { ascending: false }).limit(50)),
      mineOnly(sb.from("jobs").select(S).eq("status", "scheduled")
        .lt("scheduled_at", dayStart).order("scheduled_at", { ascending: false }).limit(50)),
      mineOnly(sb.from("jobs").select(S).eq("status", "in_progress")
        .is("scheduled_at", null).order("created_at", { ascending: false }).limit(50)),
      // Never filtered by `who`: the point is to keep offering the people
      // the current filter is hiding.
      sb.from("jobs").select("job_technicians(technician_id,technicians(name))")
        .gte("scheduled_at", recentFrom).limit(400),
    ]);

    /**
     * NEWEST FIRST, like everything else. Anthony: "in all areas that we
     * sort jobs, leads, schedule etc, I always want the most recent to
     * show first."
     *
     * These two came back in whatever order the database felt like, which
     * is not an order at all: PostgREST without an ORDER BY can hand back
     * the same rows differently on two consecutive loads, so a box meant
     * to stop work going missing was shuffling itself.
     */
    const orphans = [
      ...((dateless.data ?? []) as unknown as Booked[]),
      ...((adrift.data ?? []) as unknown as Booked[]),
    ].sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
    /**
     * A JOB WITH AN INVOICE ON IT IS NOT WAITING TO BE STARTED.
     *
     * Anthony: "IF DAY HAS GONE AND JOB IS DONE THEN SHOULD SAY PAID OR
     * SOMETHING NOT day has gone."
     *
     * Work gets finished in the bay and paid at the curb without anybody
     * moving the job off `scheduled`, and then this box shouts at somebody
     * who knows it is done. A flag that is wrong twice is a flag that gets
     * ignored, and this one exists precisely so that work cannot go quiet.
     *
     * An invoice is the second opinion, so anything carrying one drops out
     * of here. It is still visible on the day it was booked for, and the
     * railway still counts it as billed-not-paid if that is what it is.
     */
    const late = ((overdue.data ?? []) as unknown as Booked[])
      .filter((j) => (j.invoices ?? []).length === 0);

    /**
     * WHOSE WORK IS ON THE BOARD, AS A ROW YOU CAN SEE.
     *
     * Anthony: "on schedule... we cannot select which tech we are viewing
     * when in it."
     *
     * It was a small gray dropdown wedged between a stepper, a day/week
     * picker and a view toggle — four controls in one bar, and the one
     * that mattered looked like furniture. Worse, it silently REMOVED
     * itself if anything threw, so a bad moment on the network left no
     * control at all and no sign there had been one.
     *
     * Now it is a row of names above the board, each carrying the color
     * its owner's jobs are drawn in, so choosing whose day to look at and
     * reading whose job is whose are the same control. It is built from
     * the people who actually have work in view, not from thirty-one
     * names most of whom have left.
     */
    const onBoard = new Map<string, string>();
    for (const j of [...((booked.data ?? []) as unknown as Booked[]),
                     ...((overdue.data ?? []) as unknown as Booked[]),
                     ...((dateless.data ?? []) as unknown as Booked[]),
                     ...((adrift.data ?? []) as unknown as Booked[])]) {
      for (const t of j.job_technicians ?? []) {
        if (t?.technician_id && t.technicians?.name) {
          onBoard.set(t.technician_id, t.technicians.name);
        }
      }
    }
    /**
     * Anybody who has worked in the last week stays on the bar even when
     * today is not one of their days. Added AFTER the jobs in view, so the
     * people actually on this board are the ones the bar lists first.
     */
    for (const j of (lately.data ?? []) as unknown as Booked[]) {
      for (const t of j.job_technicians ?? []) {
        if (t?.technician_id && t.technicians?.name && !onBoard.has(t.technician_id)) {
          onBoard.set(t.technician_id, t.technicians.name);
        }
      }
    }

    // Whoever is being filtered to stays on the bar even when the filter
    // leaves them with nothing, or there is no way back out.
    for (const id of who) {
      if (!onBoard.has(id)) onBoard.set(id, whoNames.get(id) ?? `That ${shopWords().techLabel}`);
    }
    // Remember every name seen, so somebody deselected off an empty board
    // can still be put back.
    for (const [id, name] of onBoard) whoNames.set(id, name);

    const cb = document.getElementById("crewbar");
    if (cb) {
      cb.innerHTML = onBoard.size
        ? `<div class="crewbar">
            <button class="crewpill${who.length ? "" : " on"}" data-who="">Everybody</button>
            ${[...onBoard.entries()]
              .sort((a, b) => a[1].localeCompare(b[1]))
              .map(([id, name]) => `<button class="crewpill${
                who.includes(id) ? " on" : ""}"
                style="--crew:${crewHue(id)}" data-who="${esc(id)}"
                aria-pressed="${who.includes(id)}">
                <i class="crewdot"></i>${esc(name)}</button>`).join("")}
            ${who.length > 1
              ? `<span class="crewcount">${who.length} selected</span>` : ""}
          </div>`
        : "";
      /**
       * A NAME TOGGLES. Pressing a second name ADDS it rather than
       * replacing the first, because "David and Mike" is the question, and
       * pressing the last selected name off leaves everybody rather than
       * an empty board with no way back.
       */
      cb.querySelectorAll<HTMLButtonElement>(".crewpill").forEach((b) =>
        b.addEventListener("click", () => {
          const id = b.dataset["who"] ?? "";
          if (!id) who = [];
          else if (who.includes(id)) who = who.filter((x) => x !== id);
          else who = [...who, id];
          void schedulePage();
        }));
    }

    const settleUp = (): void => {
      document.querySelectorAll<HTMLButtonElement>("[data-settle]").forEach((b) =>
        b.addEventListener("click", async () => {
          const id = b.dataset["settle"]!;
          const seen = Number(b.dataset["total"]) || 0;
          /**
           * WHAT IT WAS AND HOW IT WAS PAID, ASKED, NEVER ASSUMED.
           *
           * Six of these nine jobs carry no priced line at all, so there
           * is nothing to invoice from and no honest way to guess. The
           * shop knows the number; this only writes it down. Recording
           * money that did not move is the one mistake in here that is
           * worse than the job sitting on the board.
           */
          const amount = prompt(
            `What did this come to? It will be invoiced and marked paid.`,
            seen > 0 ? String(seen) : "");
          if (amount === null) return;
          const n = Number(String(amount).replace(/[^0-9.]/g, ""));
          if (!(n > 0)) { alert("That is not an amount."); return; }
          const how = (prompt("How was it paid? cash, card, check or transfer",
            "cash") ?? "").trim().toLowerCase();
          const method = ["cash", "card", "check", "transfer"].includes(how)
            ? how : "other";

          b.disabled = true; b.textContent = "Settling…";
          try {
            await settleJob(id, n, method);
            void schedulePage();
          } catch (e) {
            b.disabled = false; b.textContent = "Done and paid";
            alert(e instanceof Error ? e.message : String(e));
          }
        }));
    };

    const oh = document.getElementById("orphans");
    if (oh) {
      oh.innerHTML = `
        ${late.length ? `<div class="card warnbox">
          <div class="card-head bad">Their day has gone &middot; ${late.length}</div>
          <!-- ANYTHING FINISHED HAS ALREADY LEFT THIS BOX. Anthony: "after
               a job is booked, scheduled, done, invoiced and paid they come
               off that part. Any stuck should stay there though yes."
               That was already the rule -- the box only ever holds jobs
               still at scheduled -- so a job sitting here has genuinely
               never been closed out, whatever happened in the bay. What was
               missing was a way to close it from here rather than opening
               nine job cards. -->
          <div class="quiet pad-sm">Still booked, and the slot is behind us. Nothing
            here has been finished or billed in the system, whatever happened in the
            bay. Settle it, give it a new day, or open it.</div>
          ${late.map((j) => `<div class="lateline">
            ${jobRow(j, true)}
            <div class="lateacts">
              <button class="btn sm" data-settle="${esc(j.id)}"
                data-total="${total(j)}"
                data-who="${esc(j.customers?.full_name ?? "them")}">Done and paid</button>
            </div>
          </div>`).join("")}</div>` : ""}
        ${orphans.length ? `<div class="card warnbox">
          <div class="card-head bad">On the board with no date &middot; ${orphans.length}</div>
          <div class="quiet pad-sm">Booked or under way with nothing on the calendar,
            chasing nobody. The shape that used to go quiet.</div>
          ${orphans.map((j) => jobRow(j)).join("")}</div>` : ""}`;
      settleUp();
    }

    const byDay = new Map<string, Booked[]>();
    for (const j of (booked.data ?? []) as unknown as Booked[]) {
      if (!j.scheduled_at) continue;
      const k = shopDayKey(new Date(j.scheduled_at));
      if (!byDay.has(k)) byDay.set(k, []);
      byDay.get(k)!.push(j);
    }

    const board = document.getElementById("board");
    if (!board) return;
    // The calendar always draws a month; the list obeys the range.
    board.innerHTML = viewNow() === "calendar"
      ? `<div class="card pad">${monthGrid(byDay)}</div>`
      : dayList(byDay, w.from, w.to, range === "week");

    /**
     * ANY DATE IS A WAY IN, AND IT HAS TO LOOK LIKE ONE.
     *
     * Anthony: "when I go to click the calendar I can't select the single
     * day view or click one of the leads or jobs on it."
     *
     * Both halves of that were the same missing line. Tapping a day set
     * the range to that day and redrew — but the VIEW was still
     * "calendar", and the calendar always draws a whole month whatever the
     * range. So the grid redrew identically and the tap did nothing you
     * could see. Nothing was broken underneath; the screen just never
     * changed, which from the outside is the same thing.
     *
     * A day is a list, so tapping one goes to the list. The toggle is
     * right there to go back, which is the rule this screen already
     * follows: whatever narrows you can widen with the same control.
     */
    board.querySelectorAll<HTMLElement>(".cal-cell").forEach((c) =>
      c.addEventListener("click", (ev) => {
        // A CHIP IS A JOB, NOT A DAY. The chips sit inside the day button,
        // so without this the only thing you could ever open from the
        // calendar was the day they happen to be on.
        const chip = (ev.target as HTMLElement | null)?.closest?.("[data-job]");
        const jobId = chip instanceof HTMLElement ? chip.dataset["job"] : null;
        if (jobId) { location.hash = `#/job/${jobId}`; return; }

        const k = c.dataset["day"];
        if (!k) return;
        anchor = fromKey(k);
        range = "today";
        setViewNow("list");
        void schedulePage();
      }));
  } catch (e) {
    const board = document.getElementById("board");
    if (board) board.innerHTML =
      `<div class="card"><div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}

